/**
 * Cross-cutting behaviour for every Kalpvruks HTTP call.
 *
 * The service answers an expired session with HTTP 200 and a body of
 * [{ ApiStatus: false, Remark: 'Session Expired, Re-Login' }], so axios treats it
 * as success and each call site had to recognise it by hand. That check was
 * copy-pasted into 84 files — and the ones that forgot (the navbar client
 * selector among them) simply went quiet after the session TTL, with no error and
 * no redirect.
 *
 * Handling it in one interceptor makes the behaviour uniform for every request,
 * including the call sites that never had the check, without editing them.
 */

const SESSION_EXPIRED_REMARK = 'Session Expired, Re-Login'

// Many requests are usually in flight when a session lapses. Without this guard
// each one would fire its own logout and the user could be redirected mid-render
// several times over.
let expiryNotified = false

export function resetSessionExpiryNotice() {
  expiryNotified = false
}

export function isSessionExpiredPayload(data) {
  const first = Array.isArray(data) ? data[0] : data

  return first?.ApiStatus === false && first?.Remark === SESSION_EXPIRED_REMARK
}

export function notifySessionExpired(reason) {
  if (typeof window === 'undefined' || expiryNotified) return

  expiryNotified = true

  // Mirrors what the existing handlers do: the storage write is what tells other
  // open tabs to log out too (AppBootstrap listens for both).
  try {
    localStorage.setItem('KalpSessionExpired', String(Date.now()))
  } catch (_) {
    // Private mode or blocked storage — the event below still covers this tab.
  }

  window.dispatchEvent(
    new CustomEvent('kalp:session-expired', {
      detail: { message: SESSION_EXPIRED_REMARK, reason }
    })
  )
}

/**
 * Attaches the shared response handling to an axios instance.
 *
 * Deliberately does not swallow anything: the response is passed through
 * untouched, so the per-call-site checks that already exist keep working and
 * nothing changes for code that handles expiry itself.
 */
export function attachKalpInterceptors(instance, { label = 'api' } = {}) {
  instance.interceptors.response.use(
    response => {
      if (isSessionExpiredPayload(response?.data)) {
        notifySessionExpired(`${label}:${response?.config?.url || 'unknown'}`)
      }

      return response
    },
    error => {
      // A request aborted by the caller is normal control flow (a superseded
      // search, an unmounted component) and should not be logged as a fault.
      const canceled = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError'

      if (!canceled) {
        console.error(`[${label}]`, error?.config?.url || '', error?.message || error)
      }

      return Promise.reject(error)
    }
  )

  return instance
}
